import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Home, 
  CheckCircle, 
  TrendingUp, 
  Award, 
  Users, 
  Star,
  ArrowRight,
  Clock,
  Shield,
  Phone,
  Mail,
  MapPin,
  FileText,
  PhoneCall,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { trackCTAClick, trackPageView, trackHeyflowStart } from '../utils/gtm';
import { Link } from 'react-router-dom';
import niciImage from '../styles/nici.jpg';
import trustBackgroundImage from '../styles/trust-background-building.webp';
import logoImage from '../styles/logo.png';
import jonasRashediImage from '../styles/jonas-rashedi.png';
import nicoImage from '../styles/nico.png';
import wolfgangAlberImage from '../styles/wolfgang-alber.png';
import sarahKernImage from '../styles/sarah-kern.png';
import saschaUImage from '../styles/sascha-u.png';
import beateBesemerImage from '../styles/beate-besemer.png';

// 📝 ANPASSBARE LINKS - Hier deine URLs eintragen
const FOOTER_LINKS = {
  immobilienbewertung: 'https://www.maiave.de/service/bewertung',        // Link zur Bewertungsseite
  verkauf: 'https://www.maiave.de/service/verkauf',                      // Link zur Verkaufsseite
  beratung: 'https://www.maiave.de/service/beratung',                    // Link zur Beratungsseite
  ueberuns: 'https://www.maiave.de/ueber-uns',                           // Link zur Über uns-seite
};

// 📝 TESTIMONIALS - Kundenbewertungen
const TESTIMONIALS = [
  {
    id: 1,
    name: 'Jonas Rashedi',
    location: 'Freiburg-Wiehre',
    image: null, // wird im Component gesetzt
    stars: 5,
    text: 'Ich bin rundum zufrieden mit der Zusammenarbeit. Die Bewertung meiner Immobilien war sehr fundiert, transparent und nachvollziehbar erklärt. Besonders hervorheben möchte ich das äußerst professionelle und gleichzeitig ansprechend aufbereitete Exposé, das mit viel Detailtiefe und Marktkenntnis erstellt wurde. Die Kommunikation war jederzeit zuverlässig, schnell und angenehm. Man merkt sofort, dass hier mit Erfahrung, Sorgfalt und echtem Engagement gearbeitet wird. Klare Empfehlung für alle, die eine professionelle Immobilienbewertung und eine hochwertige Vermarktungsunterlage suchen.'
  },
  {
    id: 2,
    name: 'Nico',
    location: 'Freiburg-Stühlinger',
    image: null,
    stars: 5,
    text: 'Von Anfang an ehrlich, transparent und auf Augenhöhe. Keine leeren Versprechen, sondern klare Aussagen und echte Unterstützung. Wir haben uns jederzeit gut aufgehoben gefühlt und wussten, dass unsere Interessen im Mittelpunkt stehen. Vielen Dank für die professionelle und menschliche Begleitung, absolute Empfehlung.'
  },
  {
    id: 3,
    name: 'Wolfgang Alber',
    location: 'Kirchzarten',
    image: null,
    stars: 5,
    text: 'Der Verkauf der eigenen Wohnung ist eine emotionale Achterbahnfahrt, bei der wir sehr nervös waren. Umso glücklicher sind wir, dass wir Nicole an unserer Seite hatten. Mit unendlich viel Empathie, Geduld und einer wunderbar beruhigenden Art wurde uns jede Sorge genommen. Die vielen herzlichen Telefonate und das Gefühl, niemals mit seinen Fragen allein zu sein, haben uns unglaublich gutgetan. Hier steht nicht nur das Geschäftliche, sondern der Mensch im Vordergrund. Ein riesiges Dankeschön für diese einfühlsame, ehrliche und erfolgreiche Begleitung - wir hätten uns keine bessere Unterstützung wünschen können! Maiave in Person von Nicole wird uns immer positiv in Erinnerung bleiben. Danke und die besten Wünsche für die Zukunft.'
  },
  {
    id: 4,
    name: 'Sarah Kern',
    location: 'Freiburg-Herdern',
    image: null,
    stars: 5,
    text: 'Die Zusammenarbeit mit Nicole Randoll von maiave ist von Anfang an professionell, herzlich und von gegenseitiger Wertschätzung geprägt. Die Kommunikation verläuft jederzeit auf Augenhöhe und sehr konstruktiv. Als Immobilienmaklerin schätze ich besonders die vertrauensvolle und lösungsorientierte Art der Kooperation – so macht Zusammenarbeit richtig Freude. Ich freue mich auf viele weitere gemeinsame Projekte.'
  },
  {
    id: 5,
    name: 'Sascha U',
    location: 'Freiburg-Rieselfeld',
    image: null,
    stars: 5,
    text: 'Ich kann meine Maklerin wirklich nur weiterempfehlen! Von Anfang an war sie immer zuverlässig, super freundlich und jederzeit erreichbar. Egal welche Fragen ich hatte, sie hat sich immer Zeit genommen und mir alles in Ruhe erklärt. Besonders beeindruckt hat mich, dass sie mir sogar nach dem Kauf noch bei vielen Dokumenten und organisatorischen Sachen geholfen hat, obwohl sie das gar nicht hätte machen müssen. So einen Einsatz findet man heute wirklich selten. Außerdem hat sie mich mit kleinen Aufmerksamkeiten überrascht, was ich unglaublich lieb und wertschätzend fand. Vielen Dank für die tolle Unterstützung und die angenehme Zusammenarbeit – besser hätte ich mir die Begleitung beim Wohnungskauf nicht wünschen können!'
  },
  {
    id: 6,
    name: 'Beate Besemer',
    location: 'Freiburg-Vauban',
    image: null,
    stars: 5,
    text: 'Beste Erfahrungen habe ich mit Nicole Randoll (maiave) gemacht. Der Kontakt war zuverlässig, zugewandt, angenehm freundlich und unkompliziert. Alles konnte zeitnah besprochen werden. Nicole Randoll zeichnet sich durch ein breites Fachwissen, eine hohe Kompetenz und Flexibilität aus. Auf jeden Fall eine Empfehlung.'
  }
];

const LandingPage = () => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [openAccordionIndex, setOpenAccordionIndex] = useState(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Observe all elements with animation classes
    const animatedElements = document.querySelectorAll('.fade-in, .scale-in, .stagger-item');
    animatedElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Testimonials mit Bildern
  const testimonialsWithImages = TESTIMONIALS.map((testimonial, index) => {
    const images = [jonasRashediImage, nicoImage, wolfgangAlberImage, sarahKernImage, saschaUImage, beateBesemerImage];
    return { ...testimonial, image: images[index] };
  });

  // Open testimonial modal
  const openTestimonial = (testimonial) => {
    setSelectedTestimonial(testimonial);
    document.body.style.overflow = 'hidden';
  };

  // Close testimonial modal
  const closeTestimonial = () => {
    setSelectedTestimonial(null);
    document.body.style.overflow = 'unset';
  };

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && selectedTestimonial) {
        closeTestimonial();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [selectedTestimonial]);

  // Toggle accordion item
  const toggleAccordion = (index) => {
    setOpenAccordionIndex(openAccordionIndex === index ? null : index);
  };

  const scrollToForm = () => {
    const formSection = document.getElementById('formular-section');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
      trackCTAClick('Kostenlose Bewertung', 'scroll_to_form');
    }
  };

  // Track scroll depth
  useEffect(() => {
    // Track initial page view with UTM parameters
    trackPageView(window.location.pathname);

    let scrollDepthTracked = {
      25: false,
      50: false,
      75: false,
      100: false,
    };

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;

      Object.keys(scrollDepthTracked).forEach((depth) => {
        if (scrollPercentage >= parseInt(depth) && !scrollDepthTracked[depth]) {
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'scroll_depth',
              scroll_percentage: depth,
            });
          }
          scrollDepthTracked[depth] = true;
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header-nav">
        <div className="container-custom">
          <div className="header-content">
            <div className="logo">
              <img src={logoImage} alt="maiave Logo" className="logo-image" />
            </div>
            <Button onClick={scrollToForm} className="cta-button">
              Kostenlose Bewertung
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container-custom hero-content-wrapper">
          <div className="hero-text">
            <h1 className="hero-title">
              Wie viel ist deine Immobilie wirklich wert?
            </h1>
            <p className="hero-subtitle">
              <span className="hero-subtitle-highlight">Kostenlose & professionelle Immobilienbewertung durch lokale Expertin.</span>
              <br /><br />
              Ermittle jetzt den echten Marktwert deiner Immobilie – fundiert, unverbindlich und in nur 2 Minuten angefragt.
            </p>
            <div className="hero-cta-group">
              <Button onClick={scrollToForm} className="cta-button-large hero-cta-button">
                Jetzt kostenlose Bewertung erhalten
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="hero-trust-badges">
                <div className="trust-badge">
                  <CheckCircle className="trust-icon" />
                  <span>Kostenlos</span>
                </div>
                <div className="trust-badge">
                  <CheckCircle className="trust-icon" />
                  <span>Unverbindlich</span>
                </div>
                <div className="trust-badge">
                  <CheckCircle className="trust-icon" />
                  <span>Rückmeldung in 24h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Der falsche Immobilienwert kostet Zeit und Geld</h2>
            <p className="section-subtitle">
              Viele Eigentümer kennen den tatsächlichen Wert ihrer Immobilie nicht und stehen vor diesen Risiken:
            </p>
          </div>
          
          <div className="problems-grid">
            <Card className="problem-card">
              <CardContent className="problem-card-content">
                <div className="problem-icon-wrapper problem-icon-red">
                  <TrendingUp className="problem-icon" />
                </div>
                <h3 className="problem-title">Falscher Angebotspreis</h3>
                <p className="problem-description">
                  Zu niedrig angesetzt verschenkst du bares Geld. Zu hoch angesetzt wird deine Immobilie zum Ladenhüter und verliert nachhaltig an Wert.
                </p>
              </CardContent>
            </Card>

            <Card className="problem-card">
              <CardContent className="problem-card-content">
                <div className="problem-icon-wrapper problem-icon-orange">
                  <Clock className="problem-icon" />
                </div>
                <h3 className="problem-title">Fehlende Planungssicherheit</h3>
                <p className="problem-description">
                  Egal ob Verkauf, Erbe, Scheidung oder Finanzen – ohne genauen Marktwert fehlen dir die Grundlagen für wichtige lebensverändernde Entscheidungen.
                </p>
              </CardContent>
            </Card>

            <Card className="problem-card">
              <CardContent className="problem-card-content">
                <div className="problem-icon-wrapper problem-icon-yellow">
                  <Shield className="problem-icon" />
                </div>
                <h3 className="problem-title">Aufsitzende KI-Tools</h3>
                <p className="problem-description">
                  Reine Online-Algorithmen berücksichtigen weder die Besonderheiten deiner Lage noch den individuellen Zustand deines Objekts.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Solution Section - Accordion Style */}
      <section className="solution-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Eine professionelle Wertermittlung bringt Klarheit</h2>
            <p className="section-subtitle">
              Eine fundierte Immobilienbewertung schafft die verlässliche Grundlage, die dir bei den typischen Hürden sofort Sicherheit gibt:
            </p>
          </div>

          <div className="accordion-container">
            {/* Accordion Item 1 */}
            <div 
              className={`accordion-item ${openAccordionIndex === 0 ? 'active' : ''}`}
              onClick={() => toggleAccordion(0)}
            >
              <div className="accordion-header">
                <div className="accordion-title-wrapper">
                  <TrendingUp className="accordion-icon" />
                  <h3 className="accordion-title">Der perfekte Angebotspreis</h3>
                </div>
                <ChevronRight className="accordion-chevron" />
              </div>
              <div className="accordion-content">
                <p className="accordion-text">
                  Mit dem exakten Marktwert steigst du weder zu hoch noch zu niedrig ein. Das schützt dich vor teurem Wertverlust und langem Leerstand.
                </p>
              </div>
            </div>

            {/* Accordion Item 2 */}
            <div 
              className={`accordion-item ${openAccordionIndex === 1 ? 'active' : ''}`}
              onClick={() => toggleAccordion(1)}
            >
              <div className="accordion-header">
                <div className="accordion-title-wrapper">
                  <Shield className="accordion-icon" />
                  <h3 className="accordion-title">Volle Kontrolle & Planungssicherheit</h3>
                </div>
                <ChevronRight className="accordion-chevron" />
              </div>
              <div className="accordion-content">
                <p className="accordion-text">
                  Egal ob Verkauf, Scheidung, Erbe oder Vermögensübersicht – ein realistischer Wert gibt dir ein klares Fundament für fundierte, zukunftssichere Entscheidungen.
                </p>
              </div>
            </div>

            {/* Accordion Item 3 */}
            <div 
              className={`accordion-item ${openAccordionIndex === 2 ? 'active' : ''}`}
              onClick={() => toggleAccordion(2)}
            >
              <div className="accordion-header">
                <div className="accordion-title-wrapper">
                  <FileText className="accordion-icon" />
                  <h3 className="accordion-title">Daten & Fakten statt Bauchgefühl</h3>
                </div>
                <ChevronRight className="accordion-chevron" />
              </div>
              <div className="accordion-content">
                <p className="accordion-text">
                  Gegenüber Käufern, Banken, Miterben oder Behörden hast du mit einer detaillierten Wertanalyse stets schlagkräftige Argumente auf deiner Seite.
                </p>
              </div>
            </div>

            {/* Accordion Item 4 */}
            <div 
              className={`accordion-item ${openAccordionIndex === 3 ? 'active' : ''}`}
              onClick={() => toggleAccordion(3)}
            >
              <div className="accordion-header">
                <div className="accordion-title-wrapper">
                  <CheckCircle className="accordion-icon" />
                  <h3 className="accordion-title">Berücksichtigung echter Besonderheiten</h3>
                </div>
                <ChevronRight className="accordion-chevron" />
              </div>
              <div className="accordion-content">
                <p className="accordion-text">
                  Anders als pauschale Algorithmen fließen der tatsächliche Zustand, sanierte Gewerke und Mikrolagen-Details direkt in den echten Wert ein.
                </p>
              </div>
            </div>
          </div>

          <div className="section-cta-center">
            <Button onClick={scrollToForm} className="cta-button-large solution-cta-button">
              In 2 Minuten zum wahren Verkaufswert
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Section with Background Image */}
      <section className="trust-section-bg fade-in">
        {/* Background Image with Gradient Overlay */}
        <div className="trust-bg-image" style={{backgroundImage: `url(${trustBackgroundImage})`}}>
          <div className="trust-bg-gradient-overlay"></div>
        </div>
        
        <div className="container-custom trust-content-container">
          {/* Section Header */}
          <div className="section-header text-center">
            <h2 className="section-title">Warum Eigentümer auf unsere Immobilienbewertung vertrauen</h2>
            <p className="section-subtitle">
              Lokale Marktkenntnis trifft auf detaillierte Wertanalyse – damit du den wahren Wert deiner Immobilie kennst.
            </p>
          </div>

          {/* Benefits Grid - 2x3 centered over background */}
          <div className="trust-benefits-overlay-grid">
            <Card className="trust-benefit-card-overlay stagger-item">
              <CardContent className="trust-benefit-content">
                <div className="trust-benefit-icon-wrapper">
                  <TrendingUp className="trust-benefit-icon" />
                </div>
                <h3 className="trust-benefit-title">Präzise Marktanalyse</h3>
                <p className="trust-benefit-description">
                  Wir bewerten deine Immobilie auf Basis aktueller Marktdaten, lokaler Trends und vergleichbarer Objekte in deiner Region.
                </p>
              </CardContent>
            </Card>

            <Card className="trust-benefit-card-overlay stagger-item">
              <CardContent className="trust-benefit-content">
                <div className="trust-benefit-icon-wrapper">
                  <MapPin className="trust-benefit-icon" />
                </div>
                <h3 className="trust-benefit-title">Echte Expertise vor Ort</h3>
                <p className="trust-benefit-description">
                  Wir bieten vor-Ort-Besichtigungen und berücksichtigen individuelle Werttreiber wie Modernisierungen, Bausubstanz und Mikrolage, die automatische Rechner übersehen.
                </p>
              </CardContent>
            </Card>

            <Card className="trust-benefit-card-overlay stagger-item">
              <CardContent className="trust-benefit-content">
                <div className="trust-benefit-icon-wrapper">
                  <CheckCircle className="trust-benefit-icon" />
                </div>
                <h3 className="trust-benefit-title">Klarheit für deine Zukunft</h3>
                <p className="trust-benefit-description">
                  Mit unserem detaillierten Bewertungsbericht hast du eine verlässliche Entscheidungsgrundlage für deinen nächsten Schritt.
                </p>
              </CardContent>
            </Card>

            <Card className="trust-benefit-card-overlay stagger-item">
              <CardContent className="trust-benefit-content">
                <div className="trust-benefit-icon-wrapper">
                  <FileText className="trust-benefit-icon" />
                </div>
                <h3 className="trust-benefit-title">Verständlich & Transparent</h3>
                <p className="trust-benefit-description">
                  Wir bereiten die Ergebnisse so auf, dass du genau nachvollziehen kannst, wie sich der Marktwert deiner Immobilie zusammensetzt.
                </p>
              </CardContent>
            </Card>

            <Card className="trust-benefit-card-overlay stagger-item">
              <CardContent className="trust-benefit-content">
                <div className="trust-benefit-icon-wrapper">
                  <Users className="trust-benefit-icon" />
                </div>
                <h3 className="trust-benefit-title">Weitreichendes Käufernetzwerk</h3>
                <p className="trust-benefit-description">
                  Dank unserer starken Käuferschaft verkaufen wir Immobilien oft innerhalb weniger Wochen – und begleiten dich so über die Bewertung hinaus.
                </p>
              </CardContent>
            </Card>

            <Card className="trust-benefit-card-overlay stagger-item">
              <CardContent className="trust-benefit-content">
                <div className="trust-benefit-icon-wrapper highlight-icon">
                  <Shield className="trust-benefit-icon" />
                </div>
                <h3 className="trust-benefit-title">100% Kostenlos & Unverbindlich</h3>
                <p className="trust-benefit-description">
                  Deine Bewertung ist komplett kostenfrei, vertraulich und verpflichtet dich zu nichts. Du entscheidest ganz in Ruhe, wie du die Information nutzt.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Continuous Infinite Carousel */}
      <section className="testimonials-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Das sagen unsere Kunden</h2>
            <p className="section-subtitle">
              Echte Erfahrungen von Immobilieneigentümern aus Freiburg und Umgebung
            </p>
          </div>

          <div className="testimonials-carousel-infinite">
            <div className="carousel-track-infinite">
              {/* Original testimonials */}
              {testimonialsWithImages.map((testimonial) => (
                <div 
                  key={`original-${testimonial.id}`} 
                  className="carousel-slide-infinite"
                  onClick={() => openTestimonial(testimonial)}
                >
                  <Card className="testimonial-card testimonial-card-clickable">
                    <CardContent className="testimonial-content">
                      <div className="stars">
                        {[...Array(testimonial.stars)].map((_, i) => (
                          <Star key={i} className="star-icon" fill="currentColor" />
                        ))}
                      </div>
                      <p className="testimonial-text">
                        "{testimonial.text}"
                      </p>
                      <div className="testimonial-author">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name} 
                          className="author-avatar-image" 
                        />
                        <div>
                          <div className="author-name">{testimonial.name}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
              {/* Duplicate testimonials for seamless loop */}
              {testimonialsWithImages.map((testimonial) => (
                <div 
                  key={`duplicate-${testimonial.id}`} 
                  className="carousel-slide-infinite"
                  onClick={() => openTestimonial(testimonial)}
                >
                  <Card className="testimonial-card testimonial-card-clickable">
                    <CardContent className="testimonial-content">
                      <div className="stars">
                        {[...Array(testimonial.stars)].map((_, i) => (
                          <Star key={i} className="star-icon" fill="currentColor" />
                        ))}
                      </div>
                      <p className="testimonial-text">
                        "{testimonial.text}"
                      </p>
                      <div className="testimonial-author">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name} 
                          className="author-avatar-image" 
                        />
                        <div>
                          <div className="author-name">{testimonial.name}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial Modal */}
          {selectedTestimonial && (
            <div className="testimonial-modal-overlay" onClick={closeTestimonial}>
              <div className="testimonial-modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close-button" onClick={closeTestimonial}>
                  <X className="close-icon" />
                </button>
                <div className="modal-content">
                  <div className="stars">
                    {[...Array(selectedTestimonial.stars)].map((_, i) => (
                      <Star key={i} className="star-icon" fill="currentColor" />
                    ))}
                  </div>
                  <p className="modal-testimonial-text">
                    "{selectedTestimonial.text}"
                  </p>
                  <div className="modal-author">
                    <img 
                      src={selectedTestimonial.image} 
                      alt={selectedTestimonial.name} 
                      className="author-avatar-image-large" 
                    />
                    <div>
                      <div className="author-name">{selectedTestimonial.name}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Form Section */}
      <section id="formular-section" className="form-section scale-in">
        <div className="container-custom">
          <div className="form-wrapper">
            <div className="form-header">
              <h2 className="form-title">Mache jetzt den ersten Schritt!</h2>
            </div>

            {/* Heyflow Form Embed */}
            <div className="external-form-container">
              {/* 
                HEYFLOW INTEGRATION - Schritt-für-Schritt:
                
                1. Gehe zu app.heyflow.com und öffne dein Flow
                2. Klicke auf "Share" oder "Publish"
                3. Kopiere den Embed-Code (iframe oder script)
                4. Ersetze den Kommentar unten mit deinem Heyflow-Code
                
                Beispiel Heyflow iframe:
                <iframe src="https://my.heyflow.app/DEIN-FLOW-ID" 
                        width="100%" 
                        height="600px" 
                        frameborder="0"
                        onload="trackHeyflowStart()">
                </iframe>
                
                Beispiel Heyflow script:
                <div id="heyflow-embed" data-heyflow-id="DEIN-FLOW-ID"></div>
                <script src="https://my.heyflow.app/embed.js"></script>
              */}
              
              {/* 👇 HIER DEIN HEYFLOW-CODE EINFÜGEN 👇 */}
                <heyflow-wrapper flow-id="dYIPDlR1M1xk8lN3A9C9" dynamic-height style-config='{"width":"800px"}'></heyflow-wrapper>
              {/* 👆 BIS HIER 👆 */}
            </div>

            <div className="form-footer-info">
              <div className="info-item">
                <Shield className="info-icon" />
                <span>Deine Daten sind sicher und werden vertraulich behandelt</span>
              </div>
              <div className="info-item">
                <CheckCircle className="info-icon" />
                <span>100% kostenlos und unverbindlich</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container-custom">
          <div className="footer-grid">
            <div className="footer-column">
              <div className="footer-logo">
                <img src={logoImage} alt="maiave Logo" className="footer-logo-image" />
              </div>
              <p className="footer-description">
                Deine vertrauenswürdige Partnerin für erfolgreiche Immobilienverkäufe. 
                Mit Expertise, Engagement und Erfolg.
              </p>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Kontakt</h4>
              <div className="footer-contact">
                <div className="contact-item">
                  <Phone className="contact-icon" />
                  <span>0761 55721877</span>
                </div>
                <div className="contact-item">
                  <Mail className="contact-icon" />
                  <span>nicole.randoll@maiave.de</span>
                </div>
                <div className="contact-item">
                  <MapPin className="contact-icon" />
                  <span>Kaiser-Joseph-Straße 254 <br/> 79098 Freiburg im Breisgau</span>
                </div>
              </div>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Rechtliches</h4>
              <ul className="footer-links">
                <li><Link to="/impressum">Impressum</Link></li>
                <li><Link to="/datenschutz">Datenschutz</Link></li>
                <li><Link to="/agb">Allgemeine Geschäftsbedingungen</Link></li>
                <li><Link to="/widerruf">Widerrufsrecht</Link></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Leistungen</h4>
              <ul className="footer-links">
                <li><a href={FOOTER_LINKS.immobilienbewertung}>Immobilienbewertung</a></li>
                <li><a href={FOOTER_LINKS.verkauf}>Verkauf</a></li>
                <li><a href={FOOTER_LINKS.beratung}>Beratung</a></li>
                <li><a href={FOOTER_LINKS.ueberuns}>Über uns</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 maiave. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;